import {SelectItem} from '../components/Select';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {
    BRIDGE_DEVICE_TYPE_ID_STPM,
    CONTROL_TYPE_ID_THERMOSTAT,
    ControlModel,
    GlassControlParameters,
} from '../types/control';
import {BridgeDeviceTypeModel} from '../types/bridge-device-type';
import {STPUIEntity, VirtualControlModel} from '../store/control/state';
import {useControlSelector} from './control-selector';
import {useSetSelector} from './set-selector';
import {useSelector} from 'react-redux';

export function useGlassControl(
    selected: ControlModel | VirtualControlModel | STPUIEntity,
    bridgeDeviceId: string,
    bridgeDeviceType: BridgeDeviceTypeModel,
    controlTypeId?: string) {

    const stpUIList = useSelector(state => state.control.stpUIList);
    const controlList = useControlSelector();
    const controlTypeSet = useSetSelector(state => state.control.controlTypeList);

    const isSTPUIEntity = selected?.hasOwnProperty('page_number') === true;

    const glassControlParameters: GlassControlParameters =
        !isSTPUIEntity ?
            ((selected as ControlModel)?.parameters as GlassControlParameters) :
            null;

    const [onlyEmptyPages, setOnlyEmptyPages] = useState(false);
    const pageOptionList: SelectItem<number>[] = useMemo(() => {
        const list: SelectItem<number>[] = [];
        const sameBridgeDevice = selected?.id != null && selected.bridge_device_id === bridgeDeviceId;
        const selectedPageNumber = selected?.id != null && (selected as STPUIEntity).page_number;
        // const isMediaOrAir = selected?.id != null && ((selected as STPUIEntity).type === 'media' || (selected as STPUIEntity).type === 'air');

        if (bridgeDeviceType?.page_count > 0) {
            const occupiedBySelected = sameBridgeDevice && selectedPageNumber === 0;// todo removed: && isMediaOrAir;
            let occupied = false;
            if (!occupiedBySelected) {
                occupied = stpUIList.some(stpUI =>
                    stpUI.bridge_device_id === bridgeDeviceId &&
                    stpUI.page_number === 0 &&
                    (selected == null || stpUI.relational_id !== selected.id) &&
                    (onlyEmptyPages || stpUI.type === 'media' || stpUI.type === 'air'),
                );
            }
            if (occupiedBySelected || !occupied) {
                console.log({occupiedBySelected, occupied});
                list.push({
                    value: 0,
                    label: bridgeDeviceType.has_home_page === true ?
                        'Anasayfa' :
                        '0',
                });
            } else {
                console.log({nope: true, occupiedBySelected, occupied});
            }
        }

        for (let index = 1; index < bridgeDeviceType?.page_count; index++) {
            const occupiedBySelected = sameBridgeDevice && selectedPageNumber === index; // todo removed: &&  isMediaOrAir;
            let occupied = false;
            if (!occupiedBySelected) {
                for (const stpUI of stpUIList) {
                    if (stpUI.bridge_device_id === bridgeDeviceId &&
                        stpUI.page_number === index &&
                        (onlyEmptyPages || stpUI.type === 'media' || stpUI.type === 'air')) {
                        occupied = true;
                        break;
                    }
                }
            }

            if (occupiedBySelected || !occupied) {
                list.push({value: index, label: index.toString()});
            }
        }

        return list;
    }, [bridgeDeviceId, selected, stpUIList, bridgeDeviceType?.page_count, bridgeDeviceType?.has_home_page, onlyEmptyPages]);
    const [pageNumber, setPageNumber] = useState<SelectItem<number>>(() =>
        (selected == null || !isSTPUIEntity) ? null :
            pageOptionList.find(option => option.value === (selected as STPUIEntity).page_number)
            || null,
    );

    const isThermostat = useCallback((pageNumber: number, rowNumber: number) => {
        if (bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_STPM) {
            return false;
        }

        for (const stpUI of stpUIList) {
            if (stpUI.bridge_device_id === bridgeDeviceId && stpUI.page_number === pageNumber && stpUI.row_number === rowNumber) {
                if (stpUI.type === 'control' || stpUI.type === 'virtualControl') {
                    const control = controlList.find(c => c.id === stpUI.relational_id)?.control;
                    if (control?.type_id === CONTROL_TYPE_ID_THERMOSTAT) {
                        return true;
                    }
                }
            }
        }

        return false;
    }, [bridgeDeviceId, bridgeDeviceType?.id, controlList, stpUIList]);

    const rowOptionList: SelectItem<number>[] = useMemo(() => {
        if (selected != null && !isSTPUIEntity) {
            return [];
        }

        const list: SelectItem<number>[] = [];
        const sameBridgeDevice = selected?.id != null && selected.bridge_device_id === bridgeDeviceId;
        const selectedPageNumber = selected?.id != null && (selected as STPUIEntity).page_number;
        const samePageNumber = selectedPageNumber != null && selectedPageNumber === pageNumber?.value;
        const isMediaOrAir = selected?.id != null && ((selected as STPUIEntity).type === 'media' || (selected as STPUIEntity).type === 'air');
        const selectedRowNumber = selected?.id != null && (selected as STPUIEntity).row_number;
        for (let index = 1; index <= bridgeDeviceType?.row_count; index++) {
            const occupiedBySelected = sameBridgeDevice && samePageNumber && (isMediaOrAir || selectedRowNumber === index);
            if (!occupiedBySelected) {
                const occupied = stpUIList.some(stpUI =>
                    stpUI.bridge_device_id === bridgeDeviceId &&
                    stpUI.page_number === pageNumber?.value &&
                    (stpUI.type === 'media' || stpUI.type === 'air' || stpUI.row_number === index || isThermostat(pageNumber?.value, index - 1)),
                );
                if (occupied) {
                    continue;
                }
            }

            list.push({value: index, label: (index).toString()});
        }

        return list;
    }, [isSTPUIEntity, isThermostat, selected, bridgeDeviceType?.row_count, bridgeDeviceId, pageNumber, stpUIList]);
    const [rowNumber, setRowNumber] = useState<SelectItem<number>>(() =>
        (selected == null || !isSTPUIEntity) ? null :
            rowOptionList.find(option => option.value === (selected as STPUIEntity).row_number)
            || null,
    );
    const buttonNumberOptionList: SelectItem<number>[] = useMemo(() => {
        const list: SelectItem<number>[] = [];
        const sameBridgeDevice = selected?.id != null &&
            selected.bridge_device_id === bridgeDeviceId;
        const selectedButtonNumber = selected?.id != null && glassControlParameters?.button_number;
        const controlType = controlTypeSet[controlTypeId];
        if (controlType?.render_type === 'three_phase' || controlType?.render_type === 'two_phase') {
            for (let index = 0; index < bridgeDeviceType?.button_count; index += 2) {
                const occupiedBySelected = sameBridgeDevice && selectedButtonNumber === index;
                if (!occupiedBySelected) {
                    const occupied = controlList.some(control => {
                            const itemSameBridgeDevice = control.bridgeDeviceId === bridgeDeviceId;
                            const buttonNumber = control.glassControlParameters?.button_number;
                            return itemSameBridgeDevice && (buttonNumber === index || buttonNumber === index + 1);
                        },
                    );
                    if (occupied) {
                        continue;
                    }
                }

                list.push({value: index, label: `${(index + 1).toString()}-${(index + 2).toString()}`});
            }
        } else {
            for (let index = 0; index < bridgeDeviceType?.button_count; index++) {
                const occupiedBySelected = sameBridgeDevice && selectedButtonNumber === index;
                if (!occupiedBySelected) {
                    const occupied = controlList.some(control => {
                            const itemSameBridgeDevice = control.bridgeDeviceId === bridgeDeviceId;
                            const isThreePhase = controlTypeSet[control.control.type_id]?.render_type === 'three_phase' || controlTypeSet[control.control.type_id]?.render_type === 'two_phase';
                            const buttonNumber = control.glassControlParameters?.button_number;
                            return itemSameBridgeDevice && (buttonNumber === index || (isThreePhase && buttonNumber === index - 1));
                        },
                    );
                    if (occupied) {
                        continue;
                    }
                }

                list.push({value: index, label: (index + 1).toString()});
            }
        }

        return list;
    }, [selected?.id, selected?.bridge_device_id, glassControlParameters, controlTypeSet, controlTypeId, bridgeDeviceId, bridgeDeviceType?.button_count, controlList]);
    const [buttonNumber, setButtonNumber] = useState<SelectItem<number>>(() =>
        (selected == null ? buttonNumberOptionList[0] :
            buttonNumberOptionList.find(option => option.value === glassControlParameters?.button_number)
            || null),
    );

    useEffect(() => {
        setPageNumber(pageOptionList.find(option => option.value === pageNumber?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageOptionList]);

    useEffect(() => {
        setRowNumber(rowOptionList.find(option => option.value === rowNumber?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowOptionList]);

    useEffect(() => {
        setButtonNumber(buttonNumberOptionList.find(option => option.value === buttonNumber?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buttonNumberOptionList]);


    return ({
        pageNumber, setPageNumber, pageOptionList,
        rowNumber, setRowNumber, rowOptionList,
        buttonNumber, setButtonNumber, buttonNumberOptionList,
        setOnlyEmptyPages,
    });
}
